import { Inbox, Loader2 } from "lucide-react";
import { Auth } from "aws-amplify";
import * as React from "react";
import type {
  PortalMeResponse,
  PortalProductType,
  PortalSellerGroupContext,
} from "../portal.types";
import { hostOf, track } from "@seller-hermes/app/telemetry";
import { PORTAL_PRODUCT_LABELS } from "../portal-products";
import { PortalContextCard, PortalShell, PortalState } from "./portal-selection";

const AUTO_REDIRECT_DELAY = 700;

// Elegibilidade real (products[].product === 'ROUTING') ainda não existe —
// mesmo fallback temporário usado no roteirizamo-dashboard
// (session/route.ts, eligible-contexts/route.ts). TODO(routing-eligibility):
// trocar por products?.some(p => p.product === 'ROUTING') quando esse
// critério fechar em todo lugar de uma vez.
//
// VENANCIO_CTE_RECON não é um produto habilitável por seller group
// qualquer — é um único seller group fixo (o Depot). Gate por
// NX_VENANCIO_DEPOT_SELLER_GROUP_ID (seller GROUP id), diferente do gate
// real do backend do venancio (VENANCIO_DEPOT_SELLER_ID, seller/filial id,
// via custom:seller_id do JWT) — são grãos diferentes, o que é aceitável
// como defesa em profundidade: este filtro só decide se a opção de entrada
// fria aparece pro SG; quem decide de verdade se a sessão é válida é o
// backend do venancio, filial a filial. Ver plano-sso-unificado-venancio.md
// seção A4.
const VENANCIO_DEPOT_SELLER_GROUP_ID = process.env["NX_VENANCIO_DEPOT_SELLER_GROUP_ID"] ?? "";

const isEligibleForColdEntry = (
  context: PortalSellerGroupContext,
  product: PortalProductType,
): boolean => {
  if (product === "ROUTING") return context.profile === "GO" || context.profile === "LOG";
  if (product === "VENANCIO_CTE_RECON") {
    return Boolean(VENANCIO_DEPOT_SELLER_GROUP_ID) && context.id === VENANCIO_DEPOT_SELLER_GROUP_ID;
  }
  return Boolean(context.products?.some((link) => link.product === product));
};

// O atalho "Abrir em homologacao" do card fundido (ver
// normalizePortalMeForPortal) so leva o profile em conta — nao sabe se
// aquele SG de homologacao especificamente tem o produto ativo. Reaplica a
// mesma regra de elegibilidade em cima de `homologation_products` (os
// products do SG de homologacao, carregados a parte pra nao vazar num card
// proprio) antes de decidir se o atalho aparece nesta entrada fria.
const isHomologCounterpartEligible = (
  context: PortalSellerGroupContext,
  product: PortalProductType,
): boolean =>
  Boolean(context.homologation_id) &&
  isEligibleForColdEntry(
    { ...context, products: context.homologation_products },
    product,
  );

// return_to já é a URL final do callback (mesma convenção do redirect_uri
// já usado no dashboard-web) — diferente de openPortalProduct/
// buildPortalProductHandoffUrl (fluxo quente), que recebem só a URL base do
// produto e concatenam /auth/callback. Reaproveitar aquela função aqui
// duplicaria o path. seller_group_id vai no fragmento pra garantir que o
// card clicado (não o default) seja o contexto assinado do outro lado.
const openColdEntryTarget = async (
  context: PortalSellerGroupContext,
  returnTo: string,
): Promise<void> => {
  try {
    const session = await Auth.currentSession();
    const idToken = session.getIdToken().getJwtToken();
    track({
      name: "portal_product_redirect",
      product: context.profile,
      handoff: "id_token",
      target_host: hostOf(returnTo),
    });
    window.location.href = `${returnTo}#id_token=${idToken}&seller_group_id=${context.id}`;
  } catch {
    track({
      name: "portal_product_redirect",
      product: context.profile,
      handoff: "no_session",
      target_host: hostOf(returnTo),
    });
    window.location.href = returnTo;
  }
};

interface PortalColdEntrySelectionProps {
  portalMe: PortalMeResponse;
  product: PortalProductType;
  returnTo: string;
}

// Variação de PortalSelection pra entrada fria por produto
// (?product=X&return_to=Y) — usuário chega sem sessão direto num produto
// satélite, o Portal filtra só os SGs elegíveis pra aquele produto e
// devolve o token pro return_to já validado. Não reaproveita PortalSelection
// diretamente porque o alvo do clique é diferente (return_to, não subdomínio
// LOG/GO) e a lista já vem pré-filtrada.
export const PortalColdEntrySelection = ({
  portalMe,
  product,
  returnTo,
}: PortalColdEntrySelectionProps) => {
  const eligible = React.useMemo(
    () => portalMe.seller_groups.filter((context) => isEligibleForColdEntry(context, product)),
    [portalMe.seller_groups, product],
  );

  // Não mostra os botões de produto do LOG-467 aqui — o usuário já escolheu
  // o produto ao chegar, o card não precisa oferecer os outros de novo.
  // O atalho de homologação só sobrevive se o SG de homologação
  // correspondente também for elegível para este produto (ver
  // isHomologCounterpartEligible) — senão levaria a uma conta sem acesso.
  const cardContexts = React.useMemo(
    () =>
      eligible.map((context) => {
        const showHomologShortcut = isHomologCounterpartEligible(context, product);
        return {
          ...context,
          products: undefined,
          homologation_label: showHomologShortcut ? context.homologation_label : undefined,
          homologation_id: showHomologShortcut ? context.homologation_id : undefined,
          homologation_user_seller_group_id: showHomologShortcut
            ? context.homologation_user_seller_group_id
            : undefined,
          homologation_products: undefined,
        };
      }),
    [eligible, product],
  );

  const [selectedContext, setSelectedContext] =
    React.useState<PortalSellerGroupContext | null>(null);

  const open = React.useCallback(
    (context: PortalSellerGroupContext) => {
      setSelectedContext(context);
      void openColdEntryTarget(context, returnTo);
    },
    [returnTo],
  );

  React.useEffect(() => {
    if (eligible.length !== 1) return;
    const timeoutId = window.setTimeout(() => open(eligible[0]), AUTO_REDIRECT_DELAY);
    return () => window.clearTimeout(timeoutId);
  }, [eligible, open]);

  if (eligible.length === 0) {
    return (
      <PortalShell>
        <PortalState
          icon={<Inbox size={28} aria-hidden />}
          title="Nenhum acesso disponível"
          description={`Essa conta não tem nenhum seller group com acesso ao ${PORTAL_PRODUCT_LABELS[product]}.`}
        />
      </PortalShell>
    );
  }

  if (selectedContext || eligible.length === 1) {
    const context = selectedContext ?? eligible[0];
    return (
      <PortalShell>
        <PortalState
          icon={<Loader2 className="animate-spin" size={28} aria-hidden />}
          title={`Entrando em ${context.name}...`}
          description="Redirecionando."
        />
      </PortalShell>
    );
  }

  return (
    <section className="w-full max-w-[56rem]" aria-labelledby="portal-cold-entry-title">
      <header className="mb-ab-8">
        <h1 className="text-ab-xl font-ab-semibold text-ab-fg" id="portal-cold-entry-title">
          Escolha a conta para o {PORTAL_PRODUCT_LABELS[product]}
        </h1>
      </header>
      <div className="grid gap-ab-4 sm:grid-cols-2 lg:grid-cols-3">
        {cardContexts.map((context) => (
          <PortalContextCard key={context.id} context={context} onSelect={() => open(context)} />
        ))}
      </div>
    </section>
  );
};
